import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { MenuStateService } from '../menu-state.service';

type IntakeTab = 'lines' | 'json' | 'result';
type ThemeMode = 'dark' | 'light';
type IntakeMode = 'new_trip' | 'existing_trip';

interface WarehouseOption {
  inventory_warehous_uuid?: string;
  inventory_warehouse_uuid?: string;
  inventory_warehouse_name?: string;
  warehouse_name?: string;
  [key: string]: unknown;
}

interface WarehouseCustomerOption {
  inventory_warehouse_customer_uuid?: string;
  inventory_warehouse_customer_contact_uuid?: string;
  inventory_warehouse_customer_name?: string;
  contact_organization?: string;
  [key: string]: unknown;
}

interface UploadTemplate {
  upload_template_uuid: string;
  template_type: string;
  template_key: string;
  template_name: string;
  description?: string;
  customer_contact_uuid?: string;
  parser_key: string;
  file_type: string;
  delimiter: string;
  has_header: boolean;
  sample_filename?: string;
  required_columns: string[];
  column_map: Record<string, string>;
  validation_rules: Record<string, any>;
  metadata: Record<string, any>;
  sample_content: string;
}

interface IntakeLine {
  po_number: string;
  product_model_number: string;
  line_number: string;
  package_qty: number;
  units_per_package: number;
  deliver_remainder?: number | null;
}

interface TripPackagePayload {
  customer_contact_uuid: string;
  inventory_warehouse_uuid: string;
  header: {
    po_number: string;
    trailer_id: string;
    eta: string;
  };
  lines: IntakeLine[];
}

interface IntakeIssue {
  severity: 'error' | 'warning';
  message: string;
}

@Component({
  selector: 'app-trip-package-intake',
  templateUrl: './trip-package-intake.component.html',
  styleUrls: ['./trip-package-intake.component.scss']
})
export class TripPackageIntakeComponent implements OnInit {
  intakeForm: FormGroup;

  warehouses: WarehouseOption[] = [];
  customers: WarehouseCustomerOption[] = [];
  templates: UploadTemplate[] = [];

  selectedTemplate: UploadTemplate | null = null;
  parsedPayload: TripPackagePayload | null = null;
  parsedFileName = '';
  warnings: string[] = [];
  errors: string[] = [];
  isLoadingWarehouses = false;
  isLoadingCustomers = false;
  isLoadingTemplates = false;
  isSubmitting = false;
  submitResponse: any = null;
  activeTab: IntakeTab = 'lines';
  themeMode: ThemeMode = 'dark';
  csvHeaders: string[] = [];
  rawRows: Record<string, string>[] = [];

  private readonly themeStorageKey = 'trip-package-intake-theme';

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private menuStateService: MenuStateService
  ) {
    this.intakeForm = this.fb.group({
      warehouse_uuid: ['', Validators.required],
      customer_contact_uuid: ['', Validators.required],
      template_uuid: ['', Validators.required],
      intake_mode: ['new_trip', Validators.required],
      trip_list_uuid: ['']
    });
  }

  ngOnInit(): void {
    this.initializeTheme();
    this.applyRouteContext();
    this.loadWarehouses();
  }

  get totalPackages(): number {
    return (this.parsedPayload?.lines || []).reduce((sum, line) => sum + (Number(line.package_qty) || 0), 0);
  }

  get totalUnits(): number {
    return (this.parsedPayload?.lines || []).reduce((sum, line) => sum + ((Number(line.package_qty) || 0) * (Number(line.units_per_package) || 0)), 0);
  }

  get uniquePoNumbers(): string[] {
    return Array.from(new Set((this.parsedPayload?.lines || [])
      .map(line => String(line.po_number || '').trim())
      .filter(Boolean)));
  }

  get canSubmit(): boolean {
    return this.intakeForm.valid
      && !!this.parsedPayload
      && this.errors.length === 0
      && !this.isSubmitting
      && (!this.isExistingTripMode || !!this.existingTripUuid);
  }

  get selectedWarehouseLabel(): string {
    const uuid = String(this.intakeForm.get('warehouse_uuid')?.value || '');
    const warehouse = this.warehouses.find(option => this.warehouseUuid(option) === uuid);
    return warehouse ? this.warehouseLabel(warehouse) : 'Select warehouse';
  }

  get selectedCustomerLabel(): string {
    const uuid = String(this.intakeForm.get('customer_contact_uuid')?.value || '');
    const customer = this.customers.find(option => this.customerContactUuid(option) === uuid);
    return customer ? this.customerLabel(customer) : 'Select customer';
  }

  get selectedTemplateLabel(): string {
    return this.selectedTemplate?.template_name || 'Select template';
  }

  get intakeMode(): IntakeMode {
    return this.intakeForm.get('intake_mode')?.value === 'existing_trip' ? 'existing_trip' : 'new_trip';
  }

  get isExistingTripMode(): boolean {
    return this.intakeMode === 'existing_trip';
  }

  get existingTripUuid(): string {
    return String(this.intakeForm.get('trip_list_uuid')?.value || '').trim();
  }

  get intakeModeLabel(): string {
    return this.isExistingTripMode ? 'existing trip' : 'new trip + lading';
  }

  get headerSummary(): Array<{ label: string; value: string | number }> {
    if (!this.parsedPayload) {
      return [
        { label: 'Primary PO', value: '-' },
        { label: 'POs', value: 0 },
        { label: 'Container', value: '-' },
        { label: 'ETA', value: '-' },
        { label: 'Packages', value: 0 },
        { label: 'Units', value: 0 }
      ];
    }

    return [
      { label: 'Primary PO', value: this.parsedPayload.header.po_number || '-' },
      { label: 'POs', value: this.uniquePoNumbers.length || '-' },
      { label: 'Container', value: this.parsedPayload.header.trailer_id || '-' },
      { label: 'ETA', value: this.parsedPayload.header.eta || '-' },
      { label: 'Packages', value: this.totalPackages },
      { label: 'Units', value: this.totalUnits }
    ];
  }

  get issueList(): IntakeIssue[] {
    return [
      ...this.errors.map(message => ({ severity: 'error' as const, message })),
      ...this.warnings.map(message => ({ severity: 'warning' as const, message }))
    ];
  }

  get visibleIssues(): IntakeIssue[] {
    return this.issueList.slice(0, 8);
  }

  get completedSetupSteps(): number {
    return [
      !!this.intakeForm.get('warehouse_uuid')?.value,
      !!this.intakeForm.get('customer_contact_uuid')?.value,
      !!this.selectedTemplate,
      !this.isExistingTripMode || !!this.existingTripUuid,
      !!this.parsedPayload && this.errors.length === 0
    ].filter(Boolean).length;
  }

  get requiredSetupStepCount(): number {
    return this.isExistingTripMode ? 5 : 4;
  }

  get readinessPercent(): number {
    return Math.round((this.completedSetupSteps / this.requiredSetupStepCount) * 100);
  }

  get readinessLabel(): string {
    if (this.errors.length) {
      return 'Blocked';
    }

    if (this.parsedPayload && this.isExistingTripMode && !this.existingTripUuid) {
      return 'Trip Needed';
    }

    if (this.parsedPayload && this.warnings.length) {
      return 'Review Warnings';
    }

    if (this.parsedPayload) {
      return this.isExistingTripMode ? 'Ready To Add' : 'Ready To Create';
    }

    if (this.selectedTemplate) {
      return 'Upload CSV';
    }

    return 'Setup Needed';
  }

  get readinessTone(): 'idle' | 'ready' | 'warning' | 'error' {
    if (this.errors.length) {
      return 'error';
    }

    if (this.parsedPayload && this.isExistingTripMode && !this.existingTripUuid) {
      return 'idle';
    }

    if (this.parsedPayload && this.warnings.length) {
      return 'warning';
    }

    if (this.parsedPayload) {
      return 'ready';
    }

    return 'idle';
  }

  get submitDisabledReason(): string {
    if (!this.intakeForm.get('warehouse_uuid')?.value) {
      return 'Select a warehouse';
    }

    if (!this.intakeForm.get('customer_contact_uuid')?.value) {
      return 'Select a customer';
    }

    if (!this.selectedTemplate) {
      return 'Select a template';
    }

    if (this.errors.length) {
      return this.errors[0];
    }

    if (!this.parsedPayload) {
      return this.parsedFileName ? 'Fix the CSV issue and upload again.' : 'Upload and preview a CSV';
    }

    if (this.isExistingTripMode && !this.existingTripUuid) {
      return 'Enter an existing trip UUID.';
    }

    return this.isExistingTripMode ? 'Ready to add packages to the existing trip.' : 'Ready to create trip and lading.';
  }

  get previewJson(): string {
    return JSON.stringify(this.buildSubmissionPayload(), null, 2);
  }

  get resultJson(): string {
    return JSON.stringify(this.submitResponse ?? {}, null, 2);
  }

  get resultTitle(): string {
    if (!this.submitResponse) {
      return 'No Result Yet';
    }

    return this.submitResponse?.success ? 'Intake Created' : 'Intake Failed';
  }

  loadWarehouses(): void {
    this.isLoadingWarehouses = true;
    this.http.get<any>('/api/inventory/inventory_warehouses.php').subscribe({
      next: response => {
        this.warehouses = this.asArray<WarehouseOption>(response);
        this.isLoadingWarehouses = false;
      },
      error: error => {
        console.error(error);
        this.isLoadingWarehouses = false;
        this.toastr.error('Warehouses could not be loaded.');
      }
    });
  }

  onWarehouseChange(warehouseUuid: string): void {
    this.customers = [];
    this.templates = [];
    this.selectedTemplate = null;
    this.resetParsedFile();
    this.intakeForm.patchValue({ customer_contact_uuid: '', template_uuid: '' });

    if (!warehouseUuid) return;

    this.isLoadingCustomers = true;
    this.http.get<any>(`/api/inventory/inventory_warehouse_customers.php?uuid=${encodeURIComponent(warehouseUuid)}`).subscribe({
      next: response => {
        this.customers = this.asArray<WarehouseCustomerOption>(response);
        this.isLoadingCustomers = false;
      },
      error: error => {
        console.error(error);
        this.isLoadingCustomers = false;
        this.toastr.error('Warehouse customers could not be loaded.');
      }
    });
  }

  onCustomerChange(customerContactUuid: string): void {
    this.templates = [];
    this.selectedTemplate = null;
    this.resetParsedFile();
    this.intakeForm.patchValue({ template_uuid: '' });

    if (!customerContactUuid) return;

    this.isLoadingTemplates = true;
    this.http.get<any>(`/api/upload_templates/upload_templates.php?template_type=trip_intake&customer_contact_uuid=${encodeURIComponent(customerContactUuid)}`).subscribe({
      next: response => {
        this.templates = this.asArray<UploadTemplate>(response?.data || response);
        this.isLoadingTemplates = false;

        if (this.templates.length === 1) {
          this.intakeForm.patchValue({ template_uuid: this.templates[0].upload_template_uuid });
          this.onTemplateChange(this.templates[0].upload_template_uuid);
        }
      },
      error: error => {
        console.error(error);
        this.isLoadingTemplates = false;
        this.toastr.error('Upload templates could not be loaded.');
      }
    });
  }

  onTemplateChange(templateUuid: string): void {
    this.selectedTemplate = this.templates.find(template => template.upload_template_uuid === templateUuid) || null;
    this.resetParsedFile();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!this.selectedTemplate) {
      this.toastr.warning('Select a template before uploading a file.');
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        this.parseCsv(String(reader.result || ''), file.name);
      } catch (error) {
        console.error(error);
        this.toastr.error('CSV could not be parsed.');
      }
    };
    reader.readAsText(file);
  }

  downloadTemplate(): void {
    if (!this.selectedTemplate) return;

    const blob = new Blob([this.selectedTemplate.sample_content], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = this.selectedTemplate.sample_filename || `${this.selectedTemplate.template_key}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }

  submitIntake(): void {
    if (!this.canSubmit || !this.parsedPayload) return;

    this.isSubmitting = true;
    this.submitResponse = null;

    const skipLading = this.isExistingTripMode ? '1' : '0';
    const payload = this.buildSubmissionPayload();

    this.http.post<any>(`/api/intake/trip_package_upload.php?skip_lading=${skipLading}`, payload).subscribe({
      next: response => {
        this.isSubmitting = false;
        this.submitResponse = response;
        this.activeTab = 'result';
        if (response?.success) {
          this.toastr.success(`${response.packages_inserted || this.totalPackages} packages created.`);
        } else {
          this.toastr.error(response?.message || 'Trip package upload failed.');
        }
      },
      error: error => {
        console.error(error);
        this.isSubmitting = false;
        this.toastr.error('Trip package upload failed.');
      }
    });
  }

  warehouseLabel(warehouse: WarehouseOption): string {
    return String(warehouse.inventory_warehouse_name || warehouse.warehouse_name || warehouse.inventory_warehous_uuid || 'Warehouse');
  }

  warehouseUuid(warehouse: WarehouseOption): string {
    return String(warehouse.inventory_warehous_uuid || warehouse.inventory_warehouse_uuid || '');
  }

  customerLabel(customer: WarehouseCustomerOption): string {
    return String(customer.inventory_warehouse_customer_name || customer.contact_organization || customer.inventory_warehouse_customer_contact_uuid || 'Customer');
  }

  customerContactUuid(customer: WarehouseCustomerOption): string {
    return String(customer.inventory_warehouse_customer_contact_uuid || '');
  }

  setActiveTab(tab: IntakeTab): void {
    this.activeTab = tab;
  }

  setIntakeMode(mode: IntakeMode): void {
    this.intakeForm.patchValue({ intake_mode: mode });
    this.submitResponse = null;
  }

  toggleTheme(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    window.localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  getThemeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon';
  }

  getThemeLabel(): string {
    return this.themeMode === 'dark' ? 'Light' : 'Dark';
  }

  readinessClass(): string {
    return `intake-readiness--${this.readinessTone}`;
  }

  issueClass(issue: IntakeIssue): string {
    return issue.severity === 'error' ? 'intake-issue--error' : 'intake-issue--warning';
  }

  trackByLine(_: number, line: IntakeLine): string {
    return [line.po_number, line.line_number, line.product_model_number].join('-');
  }

  trackByIssue(index: number, issue: IntakeIssue): string {
    return `${issue.severity}-${issue.message}-${index}`;
  }

  trackByHeader(_: number, header: string): string {
    return header;
  }

  rawCellValue(row: Record<string, string>, header: string): string {
    return row[this.normalizeHeader(header)] ?? '';
  }

  private applyRouteContext(): void {
    this.route.queryParamMap.subscribe(params => {
      const tripUuid = params.get('trip_list_uuid') || '';
      const mode = params.get('mode') || '';

      if (mode === 'existing_trip' || tripUuid) {
        this.intakeForm.patchValue({
          intake_mode: 'existing_trip',
          trip_list_uuid: tripUuid || this.existingTripUuid
        });
      }
    });
  }
  private buildSubmissionPayload(): any {
    if (!this.parsedPayload) {
      return {};
    }

    const lines = this.parsedPayload.lines.map(({ deliver_remainder, ...line }) => line);

    if (this.isExistingTripMode) {
      return {
        trip_list_uuid: this.existingTripUuid,
        lines
      };
    }

    return {
      ...this.parsedPayload,
      lines
    };
  }

  private parseCsv(csv: string, fileName: string): void {
    const template = this.selectedTemplate;
    if (!template) return;

    this.resetParsedFile();
    this.parsedFileName = fileName;

    const rows = this.parseDelimitedRows(csv, template.delimiter || ',')
      .filter(row => row.some(value => value.trim() !== ''));

    if (rows.length < 2) {
      this.errors.push('The CSV must include a header row and at least one data row.');
      return;
    }

    const headers = rows[0].map((header, index) => index === 0 ? header.replace(/^\uFEFF/, '').trim() : header.trim());
    const records = rows.slice(1).map(row => this.rowToRecord(headers, row));
    this.csvHeaders = headers;
    this.rawRows = records;

    this.validateRequiredColumns(template, headers);
    if (this.errors.length > 0) return;

    const map = template.column_map || {};
    const first = records[0];
    const poNumber = this.valueFor(first, map['header.po_number']);
    const trailerId = this.valueFor(first, map['header.trailer_id']);
    const etaRaw = this.valueFor(first, map['header.eta']);
    const eta = this.normalizeDate(etaRaw);

    if (!poNumber) this.errors.push('Missing purchase order in the first row.');
    if (!trailerId) this.errors.push('Missing container/trailer in the first row.');
    if (!eta) this.errors.push(`ETA "${etaRaw}" could not be converted to YYYY-MM-DD.`);

    this.validateSingleHeader(records, map, first);

    const lines = records.map((record, index) => this.recordToLine(record, map, index));
    if (this.errors.length > 0) return;

    this.parsedPayload = {
      customer_contact_uuid: String(this.intakeForm.get('customer_contact_uuid')?.value || ''),
      inventory_warehouse_uuid: String(this.intakeForm.get('warehouse_uuid')?.value || ''),
      header: {
        po_number: poNumber,
        trailer_id: trailerId,
        eta: eta
      },
      lines
    };
    this.activeTab = 'lines';
  }

  private validateRequiredColumns(template: UploadTemplate, headers: string[]): void {
    const normalizedHeaders = new Set(headers.map(header => this.normalizeHeader(header)));
    (template.required_columns || []).forEach(column => {
      if (!normalizedHeaders.has(this.normalizeHeader(column))) {
        this.errors.push(`Missing required column: ${column}`);
      }
    });
  }

  private validateSingleHeader(records: Record<string, string>[], map: Record<string, string>, first: Record<string, string>): void {
    const fields = [
      ['Container#', 'header.trailer_id'],
      ['ETA', 'header.eta']
    ];

    records.forEach((record, index) => {
      fields.forEach(([label, key]) => {
        const column = map[key];
        if (!column) return;

        const expected = this.valueFor(first, column);
        const actual = this.valueFor(record, column);
        if (this.normalizeHeader(expected) !== this.normalizeHeader(actual)) {
          this.errors.push(`Row ${index + 2} has a different ${label}; split this file into one trip per container/ETA.`);
        }
      });
    });
  }

  private recordToLine(record: Record<string, string>, map: Record<string, string>, index: number): IntakeLine {
    const model = this.valueFor(record, map['lines.product_model_number']);
    const lineNumber = this.valueFor(record, map['lines.line_number']);
    const poNumber = this.valueFor(record, map['lines.po_number'] || map['header.po_number']);
    const packageQty = this.toPositiveInt(this.valueFor(record, map['lines.package_qty']));
    const units = this.toPositiveInt(this.valueFor(record, map['lines.units_per_package']));
    const deliverRemainderValue = this.valueFor(record, map['validation.deliver_remainder']);
    const deliverRemainder = deliverRemainderValue === '' ? null : Number(deliverRemainderValue);

    if (!model) this.errors.push(`Row ${index + 2} is missing item number.`);
    if (!lineNumber) this.errors.push(`Row ${index + 2} is missing line number.`);
    if (!poNumber) this.errors.push(`Row ${index + 2} is missing purchase order.`);
    if (!packageQty) this.errors.push(`Row ${index + 2} has an invalid crate quantity.`);
    if (!units) this.errors.push(`Row ${index + 2} has invalid units.`);

    if (deliverRemainder !== null && Number.isFinite(deliverRemainder) && packageQty && units) {
      const calculated = packageQty * units;
      if (calculated !== deliverRemainder) {
        this.warnings.push(`Row ${index + 2}: Deliver remainder is ${deliverRemainder}, but Crate Quantity x Units is ${calculated}.`);
      }
    }

    return {
      po_number: poNumber,
      product_model_number: model,
      line_number: lineNumber,
      package_qty: packageQty || 0,
      units_per_package: units || 0,
      deliver_remainder: Number.isFinite(deliverRemainder) ? deliverRemainder : null
    };
  }

  private parseDelimitedRows(input: string, delimiter: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < input.length; i++) {
      const char = input[i];
      const next = input[i + 1];

      if (char === '"') {
        if (inQuotes && next === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (char === delimiter && !inQuotes) {
        row.push(current.trim());
        current = '';
        continue;
      }

      if ((char === '\n' || char === '\r') && !inQuotes) {
        if (char === '\r' && next === '\n') i++;
        row.push(current.trim());
        rows.push(row);
        row = [];
        current = '';
        continue;
      }

      current += char;
    }

    row.push(current.trim());
    rows.push(row);
    return rows;
  }

  private rowToRecord(headers: string[], row: string[]): Record<string, string> {
    const record: Record<string, string> = {};
    headers.forEach((header, index) => {
      record[this.normalizeHeader(header)] = row[index] || '';
    });
    return record;
  }

  private valueFor(record: Record<string, string>, column?: string): string {
    if (!column) return '';
    return String(record[this.normalizeHeader(column)] || '').trim();
  }

  private normalizeHeader(value: string): string {
    return String(value || '').trim().toLowerCase();
  }

  private normalizeDate(value: string): string {
    const trimmed = String(value || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

    const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (!match) return '';

    const month = Number(match[1]);
    const day = Number(match[2]);
    const yearRaw = Number(match[3]);
    const year = yearRaw < 100 ? 2000 + yearRaw : yearRaw;

    if (month < 1 || month > 12 || day < 1 || day > 31) return '';

    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private toPositiveInt(value: string): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 1) return 0;
    return Math.floor(parsed);
  }

  private asArray<T>(response: any): T[] {
    if (Array.isArray(response)) return response as T[];
    if (Array.isArray(response?.data)) return response.data as T[];
    if (response && typeof response === 'object') {
      return Object.values(response).filter(value => value && typeof value === 'object') as T[];
    }
    return [];
  }

  private resetParsedFile(): void {
    this.parsedPayload = null;
    this.parsedFileName = '';
    this.csvHeaders = [];
    this.rawRows = [];
    this.warnings = [];
    this.errors = [];
    this.submitResponse = null;
  }

  private initializeTheme(): void {
    const savedTheme = window.localStorage.getItem(this.themeStorageKey);
    if (savedTheme === 'dark' || savedTheme === 'light') {
      this.themeMode = savedTheme;
      return;
    }

    const currentTheme = this.menuStateService.themeState$.getValue();
    this.themeMode = currentTheme === 'ams-content-dark' ? 'dark' : 'light';
  }
}
